import { useState, useCallback } from 'react';
import {
  Stack, Text, Group, Button, Alert, Table, Badge, Card, Title, Divider,
} from '@/components/ui';
import { JsonInput } from '@mantine/core';
import { IconAlertCircle, IconPlus, IconClipboardCheck, IconCode } from '@tabler/icons-react';
import { useAiWorkflowService } from '@actschurch/ccms-sdk/react';
import { useAiWorkflowStore } from '../../../../stores/aiWorkflowStore';

export default function ConfirmAndCreate() {
  const workflowService = useAiWorkflowService();
  const {
    activeSessionId,
    selectedPresetName,
    targetRecordType,
    extractedData,
    setCurrentStep,
    setError,
    error,
    reset,
  } = useAiWorkflowStore();

  const [submitting, setSubmitting] = useState(false);

  const handleCreate = useCallback(async () => {
    if (!activeSessionId) return;
    setSubmitting(true);
    setError(null);

    try {
      await workflowService.createRecord(activeSessionId);
      setCurrentStep('complete');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to create record';
      setError(message);
    } finally {
      setSubmitting(false);
    }
  }, [activeSessionId, workflowService, setCurrentStep, setError]);

  const summaryFields = extractedData ? Object.entries(extractedData).slice(0, 8) : [];

  return (
    <Stack gap="lg">
      {/* Summary card */}
      <Card shadow="sm" padding="lg" radius="md" withBorder bg="blue.0">
        <Group justify="space-between" mb="sm">
          <Group gap="xs">
            <IconClipboardCheck size={20} />
            <Title order={4}>Confirm and Create</Title>
          </Group>
        </Group>
        <Divider mb="sm" />
        <Text size="sm" c="dimmed" mb="md">
          Review the summary below and create the record.
        </Text>
        <Stack gap="sm">
          <Group gap="md">
            <Text size="sm" fw={500}>Preset:</Text>
            <Text size="sm">{selectedPresetName}</Text>
          </Group>
          <Group gap="md">
            <Text size="sm" fw={500}>Target Record:</Text>
            <Badge variant="light" color="blue">{targetRecordType}</Badge>
          </Group>
        </Stack>
      </Card>

      {error && (
        <Alert icon={<IconAlertCircle size={16} />} title="Error" color="red" withCloseButton onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Key fields table in Card */}
      {summaryFields.length > 0 && (
        <Card shadow="sm" padding={0} radius="md" withBorder>
          <Group justify="space-between" p="lg" pb="sm">
            <Group gap="xs">
              <IconClipboardCheck size={20} />
              <Title order={4}>Key Fields</Title>
            </Group>
            <Badge variant="light" color="gray">{summaryFields.length} fields</Badge>
          </Group>
          <Divider />
          <Table tableVariant="striped">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Field</Table.Th>
                <Table.Th>Value</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {summaryFields.map(([key, value]) => (
                <Table.Tr key={key}>
                  <Table.Td>
                    <Text size="sm" fw={500}>
                      {key.replace(/([A-Z])/g, ' $1').replace(/_/g, ' ').replace(/^\w/, c => c.toUpperCase()).trim()}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm" lineClamp={2}>
                      {typeof value === 'object' ? JSON.stringify(value) : String(value ?? '-')}
                    </Text>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Card>
      )}

      {/* JSON preview in Card */}
      {extractedData && (
        <Card shadow="sm" padding="lg" radius="md" withBorder>
          <Group justify="space-between" mb="sm">
            <Group gap="xs">
              <IconCode size={20} />
              <Title order={4}>JSON Preview</Title>
            </Group>
          </Group>
          <Divider mb="sm" />
          <JsonInput
            value={JSON.stringify(extractedData, null, 2)}
            minRows={6}
            autosize
            readOnly
          />
        </Card>
      )}

      {/* Action buttons */}
      <Group justify="space-between">
        <Group gap="xs">
          <Button variant="default" onClick={reset}>
            Cancel
          </Button>
          <Button variant="light" onClick={() => setCurrentStep('review')}>
            Back to Review
          </Button>
        </Group>
        <Button
          onClick={handleCreate}
          loading={submitting}
          leftSection={<IconPlus size={16} />}
          color="green"
        >
          Create Record
        </Button>
      </Group>
    </Stack>
  );
}
